import { useEffect, useRef } from "react";
import ModalBackdrop from "./ui/ModalBackdrop";

export type AddMode =
  | "paste"
  | "receipt"
  | "file"
  | "record"
  | "audio"
  | "write";

interface Props {
  open: boolean;
  onClose: () => void;
  onSelect: (mode: AddMode) => void;
}

interface Option {
  mode: AddMode;
  emoji: string;
  title: string;
  subtitle: string;
}

const OPTIONS: Option[] = [
  {
    mode: "paste",
    emoji: "📋",
    title: "Paste screenshot",
    subtitle: "Paste an image from the clipboard, or drop one in",
  },
  {
    mode: "receipt",
    emoji: "📷",
    title: "Scan receipt",
    subtitle:
      "Photo or screenshot — we'll extract the vendor, date, and amount",
  },
  {
    mode: "file",
    emoji: "📄",
    title: "Upload file",
    subtitle: "PDF, Word, Excel, image — including receipts and invoices",
  },
  {
    mode: "record",
    emoji: "🎙️",
    title: "Record call",
    subtitle: "Capture audio from your microphone, auto-transcribed",
  },
  {
    mode: "audio",
    emoji: "🔊",
    title: "Upload audio file",
    subtitle: "MP3, M4A, WAV, or WebM — transcribed automatically",
  },
  {
    mode: "write",
    emoji: "✏️",
    title: "Write record",
    subtitle:
      "Manually capture a phone call, in-person conversation, or note",
  },
];

export default function AddMenu({ open, onClose, onSelect }: Props) {
  const firstButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (open) firstButtonRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <ModalBackdrop onClose={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-menu-title"
        className="add-menu-card"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="add-menu-header">
          <h2 id="add-menu-title" className="modal-title">
            Add to this case
          </h2>
          <button
            type="button"
            className="btn-icon"
            onClick={onClose}
            aria-label="Close menu"
          >
            ✕
          </button>
        </div>

        <ul className="add-menu-options">
          {OPTIONS.map((opt, i) => (
            <li key={opt.mode}>
              <button
                type="button"
                className="add-menu-option"
                ref={i === 0 ? firstButtonRef : null}
                onClick={() => onSelect(opt.mode)}
              >
                <span className="add-menu-option-emoji" aria-hidden>
                  {opt.emoji}
                </span>
                <span className="add-menu-option-text">
                  <span className="add-menu-option-title">{opt.title}</span>
                  <span className="add-menu-option-subtitle">
                    {opt.subtitle}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </ModalBackdrop>
  );
}
